import { useMemo, useRef, useState, useCallback, useEffect } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var clamp = function (value, _a) {
    var min = _a[0], max = _a[1];
    return Math.min(Math.max(value, min), max);
};
var getNearestScale = function (value, stepSize) {
    var prevStep = Math.floor(value / stepSize) * stepSize;
    var nextStep = Math.ceil(value / stepSize) * stepSize;
    return value - prevStep > nextStep - value ? nextStep : prevStep;
};

var defaultOptions = {
    prevent: true,
    touch: true,
    mouse: true,
    direction: 'both',
    maxDistance: {
        x: { max: Infinity, min: -Infinity },
        y: { max: Infinity, min: -Infinity },
    },
    stepSize: 0,
    setCSS: true,
    onStart: function () { },
    onMove: function () { },
    onEnd: function () { },
};
var useDraggable = function (options) {
    var opts = useMemo(function () {
        return __assign(__assign({}, defaultOptions), options);
    }, [options]);
    var target = useRef(null);
    var startXY = useRef([0, 0]);
    var prevPosition = useRef([0, 0]);
    var dragging = useRef(false);
    var _a = useState([0, 0]), position = _a[0], setPosition = _a[1];
    var setTransform = useCallback(function (position, transition) {
        if (opts.stepSize) {
            position = [
                getNearestScale(position[0], typeof opts.stepSize === 'object' ? opts.stepSize.x : opts.stepSize),
                getNearestScale(position[1], typeof opts.stepSize === 'object' ? opts.stepSize.y : opts.stepSize),
            ];
        }
        prevPosition.current = position;
        setPosition(position);
        if (target.current && opts.setCSS) {
            if (transition) {
                target.current.style.transition = transition;
            }
            else {
                target.current.style.transition = '';
            }
            target.current.style.transform = "translate3d(" + position[0] + "px, " + position[1] + "px, 0)";
        }
    }, [opts.setCSS, opts.stepSize]);
    var handleStart = useCallback(function (e) {
        var _a = prevPosition.current, prevX = _a[0], prevY = _a[1];
        var _b = prevPosition.current, x = _b[0], y = _b[1];
        if (e instanceof window.TouchEvent && opts.touch) {
            dragging.current = true;
            x = e.touches[0].clientX - prevX;
            y = e.touches[0].clientY - prevY;
        }
        else if (e instanceof window.MouseEvent &&
            e.button === 0 &&
            opts.mouse) {
            dragging.current = true;
            x = e.clientX - prevX;
            y = e.clientY - prevY;
        }
        else
            return;
        startXY.current = [x, y];
        opts.onStart(target, [x, y], setTransform);
    }, [opts, setTransform]);
    var handleMove = useCallback(function (e) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        if (!dragging.current)
            return;
        if (opts.prevent) {
            e.preventDefault();
        }
        var _o = startXY.current, startX = _o[0], startY = _o[1];
        var _p = prevPosition.current, x = _p[0], y = _p[1];
        if (e instanceof window.TouchEvent && opts.touch) {
            x = e.touches[0].clientX - startX;
            y = e.touches[0].clientY - startY;
        }
        else if (e instanceof window.MouseEvent &&
            e.button === 0 &&
            opts.mouse) {
            x = e.clientX - startX;
            y = e.clientY - startY;
        }
        else
            return;
        if (opts.stepSize) {
            x = getNearestScale(x, typeof opts.stepSize === 'object' ? opts.stepSize.x : opts.stepSize);
            y = getNearestScale(y, typeof opts.stepSize === 'object' ? opts.stepSize.y : opts.stepSize);
        }
        x =
            opts.direction === 'vertical'
                ? 0
                : clamp(x, [
                    (_c = (_b = (_a = opts.maxDistance) === null || _a === void 0 ? void 0 : _a.x) === null || _b === void 0 ? void 0 : _b.min) !== null && _c !== void 0 ? _c : -Infinity,
                    (_f = (_e = (_d = opts.maxDistance) === null || _d === void 0 ? void 0 : _d.x) === null || _e === void 0 ? void 0 : _e.max) !== null && _f !== void 0 ? _f : Infinity,
                ]);
        y =
            opts.direction === 'horizontal'
                ? 0
                : clamp(y, [
                    (_j = (_h = (_g = opts.maxDistance) === null || _g === void 0 ? void 0 : _g.y) === null || _h === void 0 ? void 0 : _h.min) !== null && _j !== void 0 ? _j : -Infinity,
                    (_m = (_l = (_k = opts.maxDistance) === null || _k === void 0 ? void 0 : _k.y) === null || _l === void 0 ? void 0 : _l.max) !== null && _m !== void 0 ? _m : Infinity,
                ]);
        opts.onMove(target, [x, y], setTransform);
        setTransform([x, y]);
    }, [dragging, opts, setTransform]);
    var handleEnd = useCallback(function (e) {
        if ((e instanceof window.TouchEvent && opts.touch) ||
            (e instanceof window.MouseEvent && e.button === 0 && opts.mouse)) {
            dragging.current = false;
            opts.onEnd(target, prevPosition.current, setTransform);
        }
    }, [opts, setTransform]);
    useEffect(function () {
        var node = target.current;
        if (node) {
            if (opts.touch) {
                node.addEventListener('touchstart', handleStart);
                node.addEventListener('touchmove', handleMove, {
                    passive: !opts.prevent,
                });
                document.addEventListener('touchcancel', handleEnd);
                document.addEventListener('touchend', handleEnd);
            }
            if (opts.mouse) {
                node.addEventListener('mousedown', handleStart);
                document.addEventListener('mousemove', handleMove);
                document.addEventListener('mouseup', handleEnd);
            }
        }
        return function () {
            if (node) {
                if (opts.touch) {
                    node.removeEventListener('touchstart', handleStart);
                    node.removeEventListener('touchmove', handleMove);
                    document.removeEventListener('touchcancel', handleEnd);
                }
                if (opts.mouse) {
                    node.removeEventListener('mousedown', handleStart);
                    document.removeEventListener('mousemove', handleMove);
                    document.removeEventListener('mouseup', handleEnd);
                }
            }
        };
    }, [target, opts, handleStart, handleMove, handleEnd]);
    return { target: target, position: position, setPosition: setTransform };
};

export { useDraggable as default };
